import React from "react";
import {useTranslation} from 'react-i18next';
import {Link, useSearchParams} from "react-router-dom";

export interface GalleryPaginationProps {
  pageIndex: number;
  totalCount: number;
  itemsPerPage: number;
  onSetPageIndex: (pageIndex: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
}

const GalleryPagination: React.FC<GalleryPaginationProps> = ({
  pageIndex,
  totalCount,
  itemsPerPage,
  onSetPageIndex,
  onItemsPerPageChange
}) => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const currentPage = pageIndex; // 0-based

  const getVisiblePages = (): (number | string)[] => {
    if (totalPages <= 7) {
      return Array.from({length: totalPages}, (_, i) => i);
    }

    if (currentPage <= 3) {
      return [0, 1, 2, 3, 4, '...', totalPages - 1];
    }

    if (currentPage >= totalPages - 4) {
      return [
        0,
        '...',
        totalPages - 5,
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
      ];
    }

    return [
      0,
      '...',
      currentPage - 1,
      currentPage,
      currentPage + 1,
      '...',
      totalPages - 1,
    ];
  };

  const visiblePages = getVisiblePages();

  const handlePageClick = (pageNo: number, e: React.MouseEvent) => {
    // If it's a normal click (not right-click/cmd-click), handle it via callback
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey && e.button !== 1) {
      e.preventDefault();
      onSetPageIndex(pageNo);
    }
  };

  const getPageLink = (pageNo: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', (pageNo + 1).toString());
    return {search: params.toString()};
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    onItemsPerPageChange(newItemsPerPage);
  };

  const itemsPerPageOptions = [4, 8, 12, 16, 32, 64, 128];

  const prevNextButton = (icon: string, pageNo: number, disabled: boolean) => {
    if (disabled) {
      return (
        <span
          className="px-3 py-2 rounded border bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
        >
          <i className={`text-sm fa ${icon}`}/>
        </span>
      );
    }

    return (
      <Link
        to={getPageLink(pageNo)}
        onClick={(e) => handlePageClick(pageNo, e)}
        className="px-3 py-2 rounded border bg-white border-gray-300 text-gray-600 hover:bg-gray-50 flex items-center justify-center"
      >
        <i className={`text-sm fa ${icon}`}/>
      </Link>
    );
  };

  return (
    <div className="mt-10 flex justify-between items-center">
      <div className={`flex-1 flex justify-center`}>
        <nav className={`flex items-center space-x-1 ${totalPages <= 1 ? 'hidden' : ''}`}>
          {prevNextButton(
            'fa-chevron-left',
            pageIndex - 1,
            pageIndex === 0)}

          {visiblePages.map((page, index) => {
            if (page === '...') {
              return (
                <span key={`ellipsis-${index}`} className="px-4 py-2 text-gray-500">
                  <i className="fa fa-ellipsis-h"/>
                </span>
              );
            }

            const pageNum = page as number;
            const isCurrentPage = pageNum === currentPage;

            if (isCurrentPage) {
              return (
                <span
                  key={pageNum}
                  className="px-4 py-2 rounded bg-gray-800 text-white font-medium"
                >
                  {pageNum + 1}
                </span>
              );
            }

            return (
              <Link
                key={pageNum}
                to={getPageLink(pageNum)}
                onClick={(e) => handlePageClick(pageNum, e)}
                className="px-4 py-2 rounded bg-white border border-gray-300 hover:bg-gray-50 text-gray-700"
              >
                {pageNum + 1}
              </Link>
            );
          })}

          {prevNextButton(
            'fa-chevron-right',
            pageIndex + 1,
            pageIndex >= totalPages - 1)}

        </nav>
      </div>

      <div className="flex items-center space-x-2 text-sm">
        <span className="text-gray-600">{t('gallery.pagination.itemsPerPage')}</span>
        <select
          value={itemsPerPage}
          onChange={handleItemsPerPageChange}
          className="px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:border-gray-500"
        >
          {itemsPerPageOptions.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default GalleryPagination;
